import { useCheckoutContext } from '@haus-storefront-react/checkout'
import {
  useActiveCustomer,
  useActiveOrder,
  useOrderStates,
  useShippingMethods,
  useUpdateOrder,
} from '@haus-storefront-react/hooks'
import type { CheckoutData, StepProps } from '../../types'
import {
  createOrderInputFromCustomer,
  createOrderInputFromValues,
} from '../../helper/checkout-mapper'
import { Form, Formik } from 'formik'
import styles from './CustomerDetails.module.scss'
import { Typography, Button } from '@components'
import classNames from 'classnames'
import { isYupFieldRequired } from '../../helper/is-field-required'
import { Checkbox } from '../../ui/checkbox/Checkbox'
import { FormFields } from '../../ui/form-fields/FormFields'
import {
  ADDRESS_FIELDS,
  CUSTOMER_DETAILS_FIELDS,
  createCustomerDetailsValidationSchema,
  customerDetailsInitialValues,
  mapVendureAddressToAddressData,
  type CustomerDetailsFormValues,
} from './customer-details.form'
import { formatContactForDisplay } from './customer-details.helpers'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AddressData } from '../../types'
import { AddressSection } from './AddressSection'

const CustomerDetails = ({ index }: StepProps) => {
  const { t } = useTranslation()
  const { setDataForStep, setCurrentStep } = useCheckoutContext<CheckoutData>()
  const { data: activeOrder } = useActiveOrder()
  const { data: activeCustomer } = useActiveCustomer()
  const { updateOrder, loading: isOrderUpdating, error: orderError } = useUpdateOrder()
  const { transitionOrderToState, error: transitionError } = useOrderStates()
  const { shippingMethods } = useShippingMethods()

  const userIsLoggedIn = activeCustomer !== null

  const { defaultShippingAddress, defaultBillingAddress } = useMemo(() => {
    if (!userIsLoggedIn || !activeCustomer?.addresses?.length) {
      return {
        defaultShippingAddress: null as AddressData | null,
        defaultBillingAddress: null as AddressData | null,
      }
    }

    const customerAddresses = activeCustomer.addresses
    const defaultShippingAddress =
      customerAddresses.find((a) => a.defaultShippingAddress === true) ?? customerAddresses[0]
    const defaultBillingAddress =
      customerAddresses.find((a) => a.defaultBillingAddress === true) ?? defaultShippingAddress
    return {
      defaultShippingAddress: mapVendureAddressToAddressData(defaultShippingAddress),
      defaultBillingAddress: mapVendureAddressToAddressData(defaultBillingAddress),
    }
  }, [userIsLoggedIn, activeCustomer?.addresses])

  const initialValues = useMemo(
    () => customerDetailsInitialValues(activeOrder, activeCustomer ?? undefined),
    [activeOrder, activeCustomer],
  )
  const validationSchema = useMemo(
    () => createCustomerDetailsValidationSchema(userIsLoggedIn),
    [userIsLoggedIn],
  )

  const onSubmit = async (values: CustomerDetailsFormValues) => {
    const { data, orderInput } =
      userIsLoggedIn && defaultShippingAddress && activeCustomer
        ? createOrderInputFromCustomer(
            activeCustomer,
            defaultShippingAddress,
            defaultBillingAddress ?? undefined,
          )
        : createOrderInputFromValues(values)
    setDataForStep(data, index)
    if (activeOrder?.state && activeOrder.state !== 'AddingItems') {
      await transitionOrderToState('AddingItems')
    }

    if (orderInput.customer && !userIsLoggedIn) {
      await updateOrder({ customer: orderInput.customer })
    }

    if (orderInput.shippingAddress) {
      await updateOrder({ shippingAddress: orderInput.shippingAddress })
    }

    if (orderInput.billingAddress) {
      await updateOrder({ billingAddress: orderInput.billingAddress })
    }
    //TODO remove this once we have a way to select a shipping method
    if (shippingMethods && shippingMethods.length > 0) {
      await updateOrder({ shippingMethodId: shippingMethods[0].id })
    }

    setCurrentStep(index + 1)
  }

  const handleError = (error: Error): string => {
    switch (error.message) {
      case 'EMAIL_ADDRESS_CONFLICT_ERROR':
        return t('checkout.customerDetails.emailAddressConflictError')
      default:
        return t('checkout.customerDetails.saveError')
    }
  }

  const isFieldRequired = (fieldName: string): boolean =>
    isYupFieldRequired(validationSchema, fieldName)

  return (
    <div>
      <Formik<CustomerDetailsFormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ errors, touched, values, isSubmitting }) => {
          const formErrorMessage = orderError?.message || transitionError?.message
          const formErrorId = formErrorMessage ? 'customer-details-form-error' : undefined

          return (
            <Form className={styles.form} aria-describedby={formErrorId}>
              <div className={styles.formSection}>
                <Typography variant="L" as="h3">
                  {t('checkout.customerDetails.contactInformation')}
                </Typography>
                {userIsLoggedIn && activeCustomer ? (
                  <p
                    className={styles.addressReadOnly}
                    aria-label={t('checkout.customerDetails.contactInfoReadOnly')}
                  >
                    {formatContactForDisplay(activeCustomer)}
                  </p>
                ) : (
                  <FormFields
                    config={CUSTOMER_DETAILS_FIELDS}
                    errors={errors}
                    touched={touched}
                    isFieldRequired={isFieldRequired}
                    rowClassName={styles.inputRow}
                  />
                )}
              </div>
              <AddressSection
                title={t('checkout.customerDetails.shippingAddressTitle')}
                readOnlyAriaLabel={t('checkout.customerDetails.shippingAddressReadOnly')}
                address={defaultShippingAddress}
                userIsLoggedIn={userIsLoggedIn}
                showSectionWhenNoAddress={true}
                guestContent={
                  <FormFields
                    config={ADDRESS_FIELDS}
                    prefix="shippingAddress"
                    errors={errors}
                    touched={touched}
                    isFieldRequired={isFieldRequired}
                    rowClassName={styles.inputRow}
                  />
                }
              />

              <AddressSection
                title={t('checkout.customerDetails.billingAddressTitle')}
                readOnlyAriaLabel={t('checkout.customerDetails.billingAddressReadOnly')}
                address={defaultBillingAddress}
                userIsLoggedIn={userIsLoggedIn}
                showSectionWhenNoAddress={false}
                guestContent={
                  <>
                    <div className={styles.checkboxRow}>
                      <Checkbox
                        name="useSeparateBillingAddress"
                        label={t('checkout.customerDetails.useSeparateBillingAddress')}
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                    {values?.useSeparateBillingAddress && (
                      <FormFields
                        config={ADDRESS_FIELDS}
                        prefix="billingAddress"
                        errors={errors}
                        touched={touched}
                        isFieldRequired={() => true}
                        rowClassName={styles.inputRow}
                      />
                    )}
                  </>
                }
              />

              <div className={classNames(styles.formSection, styles.buttonRow)}>
                {formErrorMessage && (
                  <div id={formErrorId} className={styles.error} role="alert">
                    <Typography variant="body">
                      {handleError(orderError || transitionError)}
                    </Typography>
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={
                    isSubmitting || isOrderUpdating || (userIsLoggedIn && !defaultShippingAddress)
                  }
                >
                  {t('checkout.customerDetails.nextStep')}
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default CustomerDetails
